import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { cx } from '@emotion/css';
import * as styles from './questionWithTextFeild.style';
import MathParser from './MathParser';
import 'mathlive';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import katex from 'katex';
import 'katex/dist/katex.min.css';


window.katex = katex;

// Helper to convert $latex$ to Quill HTML formula spans
export function convertLatexToQuillHtml(text) {
  if (!text) return '';
  let html = '';
  let i = 0;
  while (i < text.length) {
    if (text[i] !== '$') {
      const char = text[i++];
      if (char === '<') html += '&lt;';
      else if (char === '>') html += '&gt;';
      else if (char === '&') html += '&amp;';
      else html += char;
      continue;
    }
    
    // Check for block math $$...$$
    if (i + 1 < text.length && text[i + 1] === '$') {
      const closeIdx = text.indexOf('$$', i + 2);
      if (closeIdx !== -1) {
        const content = text.substring(i + 2, closeIdx);
        const escapedContent = content.replace(/"/g, '&quot;');
        html += `<span class="ql-formula" data-value="${escapedContent}"></span>`;
        i = closeIdx + 2;
        continue;
      }
    }

    // Check for inline math $...$
    let j = i + 1;
    while (j < text.length && text[j] !== '$') {
      if (text[j] === '\\') j++;
      j++;
    }
    if (j < text.length) {
      const content = text.substring(i + 1, j);
      const hasLatex = /[\\^_{}]/.test(content);
      let isMath = hasLatex;
      if (!hasLatex) {
        const validSpacing = !/^\s/.test(content) && !/\s$/.test(content);
        const noLongWords = !/[a-zA-Z]{3,}/.test(content);
        if (validSpacing && noLongWords && content.length > 0) isMath = true;
      }
      if (isMath) {
        const escapedContent = content.replace(/"/g, '&quot;');
        html += `<span class="ql-formula" data-value="${escapedContent}"></span>`;
        i = j + 1;
        continue;
      }
    }
    
    html += '$';
    i++;
  }
  return html;
}

class EditQuestionswithTextfield extends Component {
  constructor(props) {
    super(props);
    this.state = {
      question: this.props.question || '',
      questionHtml: convertLatexToQuillHtml(this.props.question || ''),
      correctanswer: this.props.correctanswer || '',
      correctanswerHtml: convertLatexToQuillHtml(this.props.correctanswer || ''),
      subcategory: this.props.subcategory || '',
      showBuilder: false,
      activeInput: null, // 'question' or 'answer'
      mathBuilderValue: '',
    };
    this.textInput = React.createRef();
    this.answerInput = React.createRef();
    this.mathFieldRef = React.createRef();
    
    // We only need the formula module for inline math
    this.quillModules = {
      toolbar: false,
      formula: true,
    };
  }

  getRawText(editor) {
    const delta = editor.getContents();
    let text = '';
    if (delta && delta.ops) {
      delta.ops.forEach(op => {
        if (typeof op.insert === 'string') {
          text += op.insert;
        } else if (op.insert && op.insert.formula) {
          text += `$${op.insert.formula}$`;
        }
      });
    }
    // Remove the trailing newline Quill always adds
    if (text.endsWith('\n')) {
      text = text.slice(0, -1);
    }
    return text;
  }

  handleQuestionChange = (content, delta, source, editor) => {
    const rawText = this.getRawText(editor);
    this.setState({ questionHtml: content, question: rawText }, () => {
      this.props.selectedanswer(
        this.state.question,
        this.state.correctanswer,
        '',
        ['1', '2', '3', '4']
      );
    });
  };

  handleAnswerChange = (content, delta, source, editor) => {
    const rawText = this.getRawText(editor);
    this.setState({ correctanswerHtml: content, correctanswer: rawText }, () => {
      this.props.selectedanswer(
        this.state.question,
        this.state.correctanswer,
        '',
        ['1', '2', '3', '4']
      );
    });
  };

  handleMathInput = (e) => {
    this.setState({ mathBuilderValue: e.target.value });
  };

  toggleBuilder = (e, field) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (this.state.showBuilder && this.state.activeInput === field) {
      this.setState({ showBuilder: false, activeInput: null }, () => {
        if (this.mathFieldRef.current) {
          this.mathFieldRef.current.removeEventListener('input', this.handleMathInput);
        }
      });
    } else {
      this.setState({ showBuilder: true, activeInput: field, mathBuilderValue: '' }, () => {
        if (this.mathFieldRef.current) {
          this.mathFieldRef.current.addEventListener('input', this.handleMathInput);
          this.mathFieldRef.current.focus();
        }
      });
    }
  };

  insertMath = () => {
    const isQuestion = this.state.activeInput === 'question';
    const quillRef = isQuestion ? this.textInput : this.answerInput;
    if (!quillRef.current) return;
    
    const editor = quillRef.current.getEditor();
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    
    editor.insertEmbed(index, 'formula', this.state.mathBuilderValue);
    editor.setSelection(index + 1);

    this.setState({ showBuilder: false, activeInput: null }, () => {
      if (this.mathFieldRef.current) {
        this.mathFieldRef.current.removeEventListener('input', this.handleMathInput);
      }
    });
  };

  componentDidUpdate(prevProps) {
    // We intentionally do NOT sync question or correctanswer from props here.
    // Since React remounts the component when the question ID changes (via key={presentquestion.id}),
    // the constructor handles initialization perfectly.
    // Syncing from props causes rapid typing to drop keystrokes due to asynchronous parent state updates
    // erasing the ReactQuill local state and resetting the cursor.
  }
  
  componentWillUnmount() {
    if (this.mathFieldRef.current) {
      this.mathFieldRef.current.removeEventListener('input', this.handleMathInput);
    }
  }

  render() {
    return (
      <div className="questionBox" style={{ padding: '30px', backgroundColor: '#fcfaf6', borderRadius: '20px', display: 'flex', gap: '40px', border: '1px solid #f0ebe1', boxShadow: '0 10px 40px rgba(0,0,0,0.03)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        
        {/* ================= LEFT: LIVE STUDENT PREVIEW ================= */}
        <div style={{ 
          flex: '1 1 400px',
          border: '1px solid #f0ebe1', 
          borderRadius: '20px', 
          padding: '30px', 
          backgroundColor: '#fff',
          position: 'relative',
          boxShadow: '0 8px 25px rgba(0,0,0,0.02)'
        }}>
          <div style={{ 
            position: 'absolute', 
            top: '-14px', 
            left: '20px', 
            backgroundColor: '#ffedd5', 
            color: '#d97706', 
            padding: '6px 16px', 
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '800',
            letterSpacing: '1px',
            textTransform: 'uppercase',
          }}>
            Live Student Preview
          </div>

          <h2 className={cx(styles.title)} style={{ margin: '20px 0 10px' }}>
            <span style={{ color: '#f97316' }}>Q{this.props.questionnumber || 1}</span><span style={{ fontSize: '24px', color: '#64748b' }}>/{this.props.totalquestions || 5}</span>
          </h2>
          <div className={cx(styles.questions)} style={{ fontSize: '22px', color: '#1e293b', fontWeight: '600', marginBottom: '30px' }}>
            <MathParser text={this.state.question || 'Type a question to see the preview...'} />
          </div>
          
          <div className={cx(styles.answers)} style={{ background: 'linear-gradient(180deg, #f97316 0%, #fbbf24 100%)', borderRadius: '30px', padding: '20px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '15px', right: '15px' }}>
              <div style={{ 
                  background: 'rgba(255,255,255,0.4)', 
                  borderRadius: '8px', 
                  padding: '4px 8px', 
                }}>
                <span style={{ fontSize: '20px', color: '#fff' }}>⌨️</span>
              </div>
            </div>
            <div className="s-title" style={{ fontSize: '15px', color: '#fff', fontWeight: '600', marginBottom: '15px' }}>Type Answer below</div>
            
            <div style={{ backgroundColor: '#fff', borderRadius: '20px', minHeight: '120px', padding: '15px', boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.05)' }}>
              <ReactQuill
                readOnly={true}
                theme="snow"
                value={this.state.correctanswerHtml}
                modules={{ toolbar: false }}
              />
            </div>
          </div>
        </div>
        {/* ============================================================ */}

        {/* ================= RIGHT: EDITOR SECTION ================= */}
        <div className="centeranswer" style={{ flex: '1 1 400px', display: 'flex', flexDirection: 'column', gap: '35px', paddingTop: '10px' }}>
          
          {/* Edit Question */}
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <i className="bx bxs-pencil" style={{ color: '#f97316', fontSize: '20px', marginRight: '8px' }}></i>
              <strong style={{ color: '#1e293b', fontSize: '16px' }}>Edit Question:</strong>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              <div style={{ flex: 1, backgroundColor: '#fff', borderRadius: '12px', border: '2px solid #f97316', padding: '5px' }}>
                <ReactQuill
                  ref={this.textInput}
                  theme="snow"
                  value={this.state.questionHtml}
                  onChange={this.handleQuestionChange}
                  modules={this.quillModules}
                  placeholder="Type your question here..."
                />
              </div>
              <div style={{ marginLeft: '12px' }}>
                <button 
                  type="button"
                  onClick={(e) => this.toggleBuilder(e, 'question')}
                  title="Toggle Math Equation Builder"
                  style={{ 
                    background: '#f97316', 
                    border: 'none', 
                    borderRadius: '12px', 
                    padding: '0 16px',
                    height: '100%',
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(249,115,22,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <span style={{ fontSize: '22px', color: '#fff' }}>⌨️</span>
                </button>
              </div>
            </div>
            
            {this.state.showBuilder && this.state.activeInput === 'question' && (
              <div 
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  zIndex: 1000,
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '16px',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                  width: '100%',
                  marginTop: '8px'
                }}
              >
                <div style={{ marginBottom: '12px', fontWeight: '600', fontSize: '15px', color: '#475569', display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '8px' }}>⌨️</span> Math Equation Builder
                </div>
                <math-field
                  ref={this.mathFieldRef}
                  style={{ 
                    width: '100%',
                    fontSize: '1.2rem',
                    padding: '10px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    color: '#0f172a',
                    outline: 'none'
                  }}
                >
                  {this.state.mathBuilderValue}
                </math-field>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', gap: '10px' }}>
                  <button 
                    type="button"
                    onClick={(e) => this.toggleBuilder(e, 'question')}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button"
                    onClick={this.insertMath}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#f97316', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600', boxShadow: '0 4px 10px rgba(249,115,22,0.3)' }}
                  >
                    Insert into Question
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Edit Answer */}
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <i className="bx bxs-check-circle" style={{ color: '#f97316', fontSize: '20px', marginRight: '8px' }}></i>
              <strong style={{ color: '#1e293b', fontSize: '16px' }}>Edit Correct Answer:</strong>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              <div style={{ flex: 1, backgroundColor: '#fcfcfc', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '5px' }}>
                <ReactQuill
                  ref={this.answerInput}
                  theme="snow"
                  value={this.state.correctanswerHtml}
                  onChange={this.handleAnswerChange}
                  modules={this.quillModules}
                  placeholder="Type the correct answer here..."
                />
              </div>
              <div style={{ marginLeft: '12px' }}>
                <button 
                  type="button"
                  onClick={(e) => this.toggleBuilder(e, 'answer')}
                  title="Toggle Math Equation Builder"
                  style={{ 
                    background: '#f97316', 
                    border: 'none', 
                    borderRadius: '12px', 
                    padding: '0 16px',
                    height: '100%',
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(249,115,22,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <span style={{ fontSize: '22px', color: '#fff' }}>⌨️</span>
                </button>
              </div>
            </div>
            
            {this.state.showBuilder && this.state.activeInput === 'answer' && (
              <div 
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  zIndex: 1000,
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '16px',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                  width: '100%',
                  marginTop: '8px'
                }}
              >
                <div style={{ marginBottom: '12px', fontWeight: '600', fontSize: '15px', color: '#475569', display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '8px' }}>⌨️</span> Math Equation Builder
                </div>
                <math-field
                  ref={this.mathFieldRef}
                  style={{ 
                    width: '100%',
                    fontSize: '1.2rem',
                    padding: '10px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    color: '#0f172a',
                    outline: 'none'
                  }}
                >
                  {this.state.mathBuilderValue}
                </math-field>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', gap: '10px' }}>
                  <button 
                    type="button"
                    onClick={(e) => this.toggleBuilder(e, 'answer')}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button"
                    onClick={this.insertMath}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#f97316', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600', boxShadow: '0 4px 10px rgba(249,115,22,0.3)' }}
                  >
                    Insert into Answer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default EditQuestionswithTextfield;
